import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import useSubscription from '../../hooks/useSubscription';
import PremiumGate from '../../components/PremiumGate';
import toast from 'react-hot-toast';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const DEFAULT_HOURS = {
  monday: { open: '09:00', close: '17:00', enabled: true },
  tuesday: { open: '09:00', close: '17:00', enabled: true },
  wednesday: { open: '09:00', close: '17:00', enabled: true },
  thursday: { open: '09:00', close: '17:00', enabled: true },
  friday: { open: '09:00', close: '17:00', enabled: true },
  saturday: { open: '09:00', close: '13:00', enabled: false },
  sunday: { open: '09:00', close: '13:00', enabled: false },
};

const formatDuration = (secs) => {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s?.toString()?.padStart(2, '0')}`;
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const deriveOutcome = (call) => {
  const analysis = call?.analysis || {};
  if (analysis?.appointment === 'true') return 'appointment';
  const summary = (analysis?.call_summary_feedback || '')?.toLowerCase();
  if (summary?.includes('callback') || summary?.includes('call back')) return 'callback';
  return 'other';
};

const deriveStatus = (call) => {
  const s = call?.status || call?.call?.status || '';
  if (s === 'no-answer' || s === 'busy' || s === 'hangup_on_voicemail') return 'missed';
  return 'completed';
};

const OUTCOME_CONFIG = {
  appointment: { label: 'Appointment', color: 'bg-green-100 text-green-700' },
  callback: { label: 'Callback', color: 'bg-blue-100 text-blue-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-600' },
};

// All Synthflow API calls go through the edge function proxy to avoid CORS
const synthflowProxy = async (action, { agent_id, payload, call_id } = {}) => {
  const { data: { session } } = await supabase?.auth?.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const res = await fetch(`${supabaseUrl}/functions/v1/synthflow-proxy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify({ action, agent_id, payload, call_id }),
  });

  const data = await res?.json();
  if (!res?.ok) {
    const errMsg = data?.error || `Proxy error ${res?.status}`;
    const details = data?.details ? ` — ${JSON.stringify(data?.details)}` : '';
    throw new Error(errMsg + details);
  }
  return data;
};

const fetchCallDetail = async (call_id) => {
  try {
    const detail = await synthflowProxy('get_call_detail', { call_id });
    return detail;
  } catch (err) {
    console.warn('Could not fetch call detail:', err?.message);
    return null;
  }
};

const AIVoicePage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { profile } = useAuth();
  const { canAccess, isOrgLoading } = useSubscription();
  const [activeTab, setActiveTab] = useState('setup');
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [agentConfig, setAgentConfig] = useState(null);
  const [agentPhoneNumber, setAgentPhoneNumber] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [fetchingTranscript, setFetchingTranscript] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);
  const [voiceGender, setVoiceGender] = useState('female');
  const [greetingMessage, setGreetingMessage] = useState('');

  const fetchAgent = useCallback(async () => {
    if (!profile?.org_id) return;

    // First check organizations table for synthflow_agent_id
    const { data: orgData } = await supabase
      ?.from('organizations')
      ?.select('synthflow_agent_id')
      ?.eq('id', profile?.org_id)
      ?.single();

    // Then fetch full agent config from ai_voice_agents
    const { data } = await supabase
      ?.from('ai_voice_agents')
      ?.select('*')
      ?.eq('org_id', profile?.org_id)
      ?.single();

    if (data) {
      // Merge synthflow_agent_id from organizations if present
      const mergedData = {
        ...data,
        agent_id: orgData?.synthflow_agent_id || data?.agent_id,
      };
      setAgentConfig(mergedData);
      setBusinessName(data?.business_name || '');
      setBusinessDescription(data?.business_description || '');
      setBusinessHours(data?.business_hours || DEFAULT_HOURS);
      setVoiceGender(data?.voice_gender || 'female');
      setGreetingMessage(data?.greeting_message || '');

      // Fetch phone number from Synthflow if agent is active
      const agentId = orgData?.synthflow_agent_id || data?.agent_id;
      if (agentId && data?.agent_status === 'active') {
        try {
          // First try get_phone_numbers (more reliable for assigned numbers)
          const phoneData = await synthflowProxy('get_phone_numbers', { agent_id: agentId });
          if (phoneData?.phone_number) {
            setAgentPhoneNumber(phoneData?.phone_number);
          } else {
            // Fallback: try get_agent
            const agentDetails = await synthflowProxy('get_agent', { agent_id: agentId });
            if (agentDetails?.phone_number) {
              setAgentPhoneNumber(agentDetails?.phone_number);
            }
          }
        } catch (err) {
          // Phone number fetch is non-critical
          console.warn('Could not fetch agent phone number:', err?.message);
        }
      }
    } else if (orgData?.synthflow_agent_id) {
      // organizations has agent_id but no ai_voice_agents record yet
      setAgentConfig({ agent_id: orgData?.synthflow_agent_id, agent_status: 'active' });
    }
  }, [profile?.org_id]);

  const fetchCalls = useCallback(async () => {
    if (!agentConfig?.agent_id) return;
    setLoading(true);
    try {
      const data = await synthflowProxy('get_calls', { agent_id: agentConfig?.agent_id });
      const rawCalls = data?.calls || [];
      // Normalize Synthflow call format — check all possible transcript locations
      const normalized = rawCalls?.map((c) => {
        // Edge function may have pre-normalized transcript into _normalized_transcript
        let transcript = c?._normalized_transcript || c?.transcript || c?.call?.transcript || c?.data?.transcript || null;
        // Also check messages array (some Synthflow plans return transcript as message objects)
        if (!transcript && Array.isArray(c?.messages) && c?.messages?.length > 0) {
          transcript = c?.messages?.map((m) => `${m?.role === 'assistant' ? 'Agent' : 'Caller'}: ${m?.content || m?.text || ''}`)?.filter(Boolean)?.join('\n');
        }
        if (!transcript && Array.isArray(c?.transcript_messages) && c?.transcript_messages?.length > 0) {
          transcript = c?.transcript_messages?.map((m) => `${m?.role === 'assistant' ? 'Agent' : 'Caller'}: ${m?.content || m?.text || ''}`)?.filter(Boolean)?.join('\n');
        }
        return {
          id: c?.call_id,
          caller_phone: c?.phone_number_from || 'Unknown',
          duration_seconds: c?.duration || c?.telephony_duration || 0,
          transcript,
          ai_summary: c?.analysis?.call_summary_feedback || null,
          status: deriveStatus(c),
          outcome: deriveOutcome(c),
          created_at: c?.telephony_start || c?.start_time || new Date()?.toISOString(),
          recording_url: c?.recording_url || null,
        };
      });
      setCalls(normalized);
    } catch (err) {
      toast?.error('Failed to load calls: ' + err?.message);
    } finally {
      setLoading(false);
    }
  }, [agentConfig?.agent_id]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  useEffect(() => {
    if (agentConfig?.agent_id) {
      fetchCalls();
    }
  }, [agentConfig?.agent_id, fetchCalls]);

  // Build Synthflow agent prompt from business info
  const buildPrompt = () => {
    const hoursText = DAYS?.filter((d) => businessHours?.[d]?.enabled)?.map((d) => `${d}: ${businessHours?.[d]?.open}–${businessHours?.[d]?.close}`)?.join(', ');
    return `You are a professional AI receptionist for ${businessName}. ${businessDescription ? businessDescription + '.' : ''} Business hours: ${hoursText || 'Monday–Friday 9am–5pm'}. Help callers with inquiries, schedule appointments, and take callback requests. Be friendly, professional, and concise.`;
  };

  const handleSaveAgent = async () => {
    if (!profile?.org_id) {
      toast?.error('No organization found. Please complete onboarding first.');
      return;
    }
    if (!businessName?.trim()) {
      toast?.error('Business name is required');
      return;
    }
    setSaving(true);
    try {
      const webhookUrl = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/voice-webhook?org_id=${profile?.org_id}`;
      const agentPayload = {
        name: businessName,
        type: 'inbound',
        description: businessDescription || '',
        external_webhook_url: webhookUrl,
        is_recording: true,
        agent: {
          prompt: buildPrompt(),
          greeting_message: greetingMessage || `Thank you for calling ${businessName}! How can I help you today?`,
          greeting_message_mode: 'agent_static',
          language: 'en-US',
          llm: 'gpt-4o',
          voice_id: voiceGender === 'female' ? 'EXAVITQu4vr4xnSDxMaL' : 'VR6AewLTigWG4xSOukaG',
          patience_level: 'medium',
        },
      };

      let synthflowModelId = agentConfig?.agent_id;

      if (synthflowModelId) {
        // Update existing agent via proxy
        await synthflowProxy('update_agent', {
          agent_id: synthflowModelId,
          payload: agentPayload,
        });
      } else {
        // Create NEW dedicated agent for this organization via proxy
        const result = await synthflowProxy('create_agent', { payload: agentPayload });
        synthflowModelId = result?.model_id;
        if (!synthflowModelId) throw new Error('No model_id returned from Synthflow');
      }

      // Save to ai_voice_agents table
      const dbPayload = {
        org_id: profile?.org_id,
        business_name: businessName,
        business_description: businessDescription,
        business_hours: businessHours,
        voice_gender: voiceGender,
        greeting_message: greetingMessage,
        agent_id: synthflowModelId,
        agent_status: 'active',
        updated_at: new Date()?.toISOString(),
      };

      if (agentConfig?.id) {
        const { error } = await supabase?.from('ai_voice_agents')?.update(dbPayload)?.eq('id', agentConfig?.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase?.from('ai_voice_agents')?.insert(dbPayload)?.select()?.single();
        if (error) throw error;
        setAgentConfig(data);
      }

      toast?.success(agentConfig?.id ? 'Agent updated successfully!' : 'AI Voice agent activated successfully!');
      setIsEditMode(false);
      fetchAgent();
    } catch (err) {
      console.error('handleSaveAgent error:', err);
      toast?.error('Failed to save agent: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateAgent = async () => {
    if (!agentConfig?.agent_id) return;
    if (!window.confirm('Are you sure you want to deactivate your AI Voice agent? Callers will no longer be answered by the AI.')) return;
    setDeactivating(true);
    try {
      await synthflowProxy('deactivate_agent', { agent_id: agentConfig?.agent_id });
      setAgentConfig(null);
      setAgentPhoneNumber(null);
      setIsEditMode(false);
      toast?.success('AI Voice agent deactivated.');
    } catch (err) {
      toast?.error('Failed to deactivate agent: ' + err?.message);
    } finally {
      setDeactivating(false);
    }
  };

  const handleCallSelect = async (call) => {
    setSelectedCall(call);
    setFetchingTranscript(false);
    // If transcript is missing, fetch full call details
    if (!call?.transcript && call?.id) {
      setFetchingTranscript(true);
      try {
        const detail = await synthflowProxy('get_call_detail', { call_id: call?.id });
        // detail.transcript is extracted by the edge function from response.calls[0].transcript
        const fullTranscript = detail?.transcript || null;
        const fullSummary = detail?.ai_summary || detail?.call?.analysis?.call_summary_feedback || null;
        setSelectedCall((prev) => ({
          ...prev,
          transcript: fullTranscript,
          ai_summary: prev?.ai_summary || fullSummary,
        }));
      } catch (err) {
        console.warn('Could not fetch call detail:', err?.message);
      } finally {
        setFetchingTranscript(false);
      }
    }
  };

  const handleHoursChange = (day, field, value) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: { ...prev?.[day], [field]: value },
    }));
  };

  const filteredCalls = calls?.filter((call) => {
    if (filter === 'Missed' && call?.status !== 'missed') return false;
    if (filter === 'Appointments' && call?.outcome !== 'appointment') return false;
    if (filter === 'Callbacks' && call?.outcome !== 'callback') return false;
    if (search) {
      const q = search?.toLowerCase();
      return call?.caller_phone?.toLowerCase()?.includes(q);
    }
    return true;
  });

  const todayStart = new Date();
  todayStart?.setHours(0, 0, 0, 0);
  const todayCalls = calls?.filter((c) => new Date(c?.created_at) >= todayStart);
  const totalToday = todayCalls?.length;
  const missedToday = todayCalls?.filter((c) => c?.status === 'missed')?.length;
  const appointmentsToday = todayCalls?.filter((c) => c?.outcome === 'appointment')?.length;

  const isAgentActive = agentConfig?.agent_status === 'active' && agentConfig?.agent_id;
  const showSetupForm = !isAgentActive || isEditMode;

  return (
    <>
      <Helmet>
        <title>AI Voice — Wilcart CRM</title>
        <meta name="description" content="AI-powered voice assistant for your business." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main className="lg:ml-64 pt-24 lg:pt-28">
          {isOrgLoading ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !canAccess('aiVoice') ? (
            <div className="p-4 lg:p-6">
              <PremiumGate
                featureName="AI Voice"
                featureIcon="Mic"
                description="Automate your inbound and outbound calls with an AI voice agent — with call transcripts, sentiment analysis, and smart scripts."
                requiredPlan="CRM + AI Voice"
                planId="crm_voice"
              />
            </div>
          ) : (
          <div className="p-4 lg:p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center">
                  <Icon name="Mic" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">AI Voice</h1>
                  <p className="text-sm text-muted-foreground">Automated voice assistant for your business</p>
                </div>
              </div>
              {isAgentActive && (
                <span className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  <span>Agent Active</span>
                </span>
              )}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Calls Today', value: totalToday, icon: 'Phone', color: 'bg-violet-100 text-violet-600' },
                { label: 'Missed Today', value: missedToday, icon: 'PhoneMissed', color: 'bg-red-100 text-red-600' },
                { label: 'Appointments', value: appointmentsToday, icon: 'CalendarCheck', color: 'bg-green-100 text-green-600' },
              ]?.map((stat) => (
                <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat?.color}`}>
                      <Icon name={stat?.icon} size={18} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                      <p className="text-xs text-muted-foreground">{stat?.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border mb-6">
              {[['setup', 'Setup', 'Settings'], ['calls', 'Call Log', 'List']]?.map(([key, label, icon]) => (
                <button key={key} onClick={() => setActiveTab(key)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === key ? 'border-violet-600 text-violet-600' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}>
                  <Icon name={icon} size={16} />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Setup Tab */}
            {activeTab === 'setup' && (
              <div className="max-w-2xl space-y-6">

                {/* ── ACTIVE STATE ── shown when agent is active and not in edit mode */}
                {isAgentActive && !isEditMode && (
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    {/* Active header */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100 p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <Icon name="Mic" size={20} className="text-green-600" />
                          </div>
                          <div>
                            <h2 className="text-base font-semibold text-foreground">{agentConfig?.business_name || 'AI Voice Agent'}</h2>
                            <div className="flex items-center space-x-1.5 mt-0.5">
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                              <span className="text-sm font-medium text-green-700">Agent Active</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Phone Number */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
                            <Icon name="Phone" size={16} className="text-violet-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Agent Phone Number</p>
                            {agentPhoneNumber ? (
                              <p className="text-base font-semibold text-foreground font-mono">{agentPhoneNumber}</p>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">Phone number assigned by Wilcart AI</p>
                            )}
                          </div>
                        </div>
                        {agentPhoneNumber && (
                          <button
                            onClick={() => { navigator.clipboard?.writeText(agentPhoneNumber); toast?.success('Phone number copied!'); }}
                            className="p-2 rounded-lg hover:bg-muted transition-colors"
                            title="Copy phone number"
                          >
                            <Icon name="Copy" size={14} className="text-muted-foreground" />
                          </button>
                        )}
                      </div>

                      {/* Agent ID */}
                      <div className="flex items-center space-x-3 p-4 bg-muted/50 rounded-xl">
                        <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
                          <Icon name="Cpu" size={16} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Agent ID</p>
                          <p className="text-sm text-foreground font-mono truncate">{agentConfig?.agent_id}</p>
                        </div>
                      </div>

                      {/* Webhook URL */}
                      <div className="flex items-start space-x-3 p-4 bg-violet-50 border border-violet-100 rounded-xl">
                        <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="Webhook" size={16} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-violet-700 font-medium uppercase tracking-wide mb-1">Webhook Endpoint</p>
                          <code className="text-xs text-violet-800 font-mono break-all">
                            {import.meta.env?.VITE_SUPABASE_URL}/functions/v1/voice-webhook?org_id={profile?.org_id}
                          </code>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={() => setIsEditMode(true)}
                          className="flex-1 flex items-center justify-center space-x-2 py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="Settings" size={15} />
                          <span>Edit Settings</span>
                        </button>
                        <button
                          onClick={handleDeactivateAgent}
                          disabled={deactivating}
                          className="flex-1 flex items-center justify-center space-x-2 py-2.5 border border-red-200 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          {deactivating ? (
                            <>
                              <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                              <span>Deactivating...</span>
                            </>
                          ) : (
                            <>
                              <Icon name="PowerOff" size={15} />
                              <span>Deactivate</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SETUP / EDIT FORM ── shown when no agent or in edit mode */}
                {showSetupForm && (
                  <>
                    {/* Edit mode header */}
                    {isEditMode && (
                      <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center space-x-2">
                          <Icon name="Settings" size={16} className="text-amber-600" />
                          <span className="text-sm font-medium text-amber-800">Editing Agent Settings</span>
                        </div>
                        <button
                          onClick={() => setIsEditMode(false)}
                          className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center space-x-1"
                        >
                          <Icon name="X" size={14} />
                          <span>Cancel</span>
                        </button>
                      </div>
                    )}

                    {/* Business Info */}
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
                        <Icon name="Building2" size={18} className="text-violet-500" />
                        <span>Business Information</span>
                      </h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Business Name *</label>
                          <input
                            type="text"
                            value={businessName}
                            onChange={(e) => setBusinessName(e?.target?.value)}
                            placeholder="e.g. Wilcart HVAC Services"
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Business Description</label>
                          <textarea
                            value={businessDescription}
                            onChange={(e) => setBusinessDescription(e?.target?.value)}
                            placeholder="Describe your business and services..."
                            rows={3}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Hours */}
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
                        <Icon name="Clock" size={18} className="text-violet-500" />
                        <span>Business Hours</span>
                      </h2>
                      <div className="space-y-3">
                        {DAYS?.map((day) => (
                          <div key={day} className="flex items-center space-x-3">
                            <div className="w-12">
                              <span className="text-sm font-medium text-foreground capitalize">{DAY_LABELS?.[day]}</span>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={businessHours?.[day]?.enabled || false}
                                onChange={(e) => handleHoursChange(day, 'enabled', e?.target?.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-violet-600" />
                            </label>
                            {businessHours?.[day]?.enabled ? (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="time"
                                  value={businessHours?.[day]?.open || '09:00'}
                                  onChange={(e) => handleHoursChange(day, 'open', e?.target?.value)}
                                  className="px-2 py-1 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                                />
                                <span className="text-muted-foreground text-sm">—</span>
                                <input
                                  type="time"
                                  value={businessHours?.[day]?.close || '17:00'}
                                  onChange={(e) => handleHoursChange(day, 'close', e?.target?.value)}
                                  className="px-2 py-1 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                                />
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">Closed</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Voice & Greeting */}
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
                        <Icon name="Mic2" size={18} className="text-violet-500" />
                        <span>Voice & Greeting</span>
                      </h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Voice Type</label>
                          <div className="flex space-x-3">
                            {[['female', 'Female', 'User'], ['male', 'Male', 'UserCheck']]?.map(([val, label, icon]) => (
                              <button
                                key={val}
                                onClick={() => setVoiceGender(val)}
                                className={`flex-1 flex items-center justify-center space-x-2 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                                  voiceGender === val
                                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                                    : 'border-border text-muted-foreground hover:border-violet-300'
                                }`}
                              >
                                <Icon name={icon} size={16} />
                                <span>{label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Greeting Message</label>
                          <textarea
                            value={greetingMessage}
                            onChange={(e) => setGreetingMessage(e?.target?.value)}
                            placeholder="e.g. Thank you for calling! How can I help you today?"
                            rows={3}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                          <p className="text-xs text-muted-foreground mt-1">This is the first message callers will hear.</p>
                        </div>
                      </div>
                    </div>

                    {/* Webhook Info */}
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-5">
                      <div className="flex items-start space-x-3">
                        <Icon name="Webhook" size={18} className="text-violet-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-violet-800 mb-1">Webhook Endpoint</h3>
                          <p className="text-xs text-violet-700 mb-2">This URL is automatically configured on your voice agent:</p>
                          <code className="block text-xs bg-white border border-violet-200 rounded px-3 py-2 text-violet-800 font-mono break-all">
                            {import.meta.env?.VITE_SUPABASE_URL}/functions/v1/voice-webhook?org_id={profile?.org_id}
                          </code>
                          {agentConfig?.agent_id && (
                            <p className="text-xs text-violet-600 mt-2">Agent ID: <span className="font-mono">{agentConfig?.agent_id}</span></p>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleSaveAgent}
                      disabled={saving}
                      className="w-full py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>{agentConfig?.id ? 'Updating...' : 'Activating...'}</span>
                        </>
                      ) : (
                        <>
                          <Icon name="Zap" size={16} />
                          <span>{agentConfig?.id ? 'Update Agent' : 'Activate Agent'}</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Call Log Tab */}
            {activeTab === 'calls' && (
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Call List */}
                <div className="w-full lg:w-80 flex-shrink-0">
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-foreground">{calls?.length} calls</span>
                        <button
                          onClick={fetchCalls}
                          disabled={loading}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                          title="Refresh"
                        >
                          <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin text-violet-500' : 'text-muted-foreground'} />
                        </button>
                      </div>
                      <div className="relative mb-3">
                        <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e?.target?.value)}
                          placeholder="Search by phone..."
                          className="w-full pl-9 pr-3 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                        />
                      </div>
                      <div className="flex space-x-1">
                        {['All', 'Missed', 'Appointments', 'Callbacks']?.map((f) => (
                          <button key={f} onClick={() => setFilter(f)}
                            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              filter === f ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:bg-muted'
                            }`}>
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[500px]">
                      {loading ? (
                        <div className="p-4 space-y-3">
                          {[1, 2, 3]?.map((i) => (
                            <div key={i} className="animate-pulse h-16 bg-muted rounded-lg" />
                          ))}
                        </div>
                      ) : !agentConfig?.agent_id ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                          <Icon name="PhoneOff" size={28} className="text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">No agent configured yet</p>
                          <button onClick={() => setActiveTab('setup')} className="text-xs text-violet-600 hover:underline mt-1">
                            Set up your agent first
                          </button>
                        </div>
                      ) : filteredCalls?.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                          <Icon name="PhoneOff" size={28} className="text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">No calls found</p>
                        </div>
                      ) : (
                        filteredCalls?.map((call) => {
                          const isSelected = selectedCall?.id === call?.id;
                          const outcomeCfg = OUTCOME_CONFIG?.[call?.outcome] || OUTCOME_CONFIG?.other;
                          return (
                            <button
                              key={call?.id}
                              onClick={() => handleCallSelect(call)}
                              className={`w-full text-left p-4 border-b border-border transition-colors hover:bg-muted/50 ${
                                isSelected ? 'bg-violet-50 border-l-2 border-l-violet-600' : ''
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  call?.status === 'missed' ? 'bg-red-100' : 'bg-green-100'
                                }`}>
                                  <Icon name={call?.status === 'missed' ? 'PhoneMissed' : 'PhoneIncoming'} size={14}
                                    className={call?.status === 'missed' ? 'text-red-500' : 'text-green-600'} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-foreground truncate">{call?.caller_phone || 'Unknown'}</p>
                                    <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">{formatTimeAgo(call?.created_at)}</span>
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-xs text-muted-foreground">{formatDuration(call?.duration_seconds)}</span>
                                    <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${outcomeCfg?.color}`}>
                                      {outcomeCfg?.label}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {/* Call Detail */}
                <div className="flex-1">
                  {selectedCall ? (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                      <div className="p-5 border-b border-border">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h2 className="text-base font-semibold text-foreground">{selectedCall?.caller_phone || 'Unknown'}</h2>
                            <p className="text-sm text-muted-foreground">{new Date(selectedCall?.created_at)?.toLocaleString()}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                              selectedCall?.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {selectedCall?.status === 'missed' ? 'Missed' : 'Completed'}
                            </span>
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${OUTCOME_CONFIG?.[selectedCall?.outcome]?.color || 'bg-gray-100 text-gray-600'}`}>
                              {OUTCOME_CONFIG?.[selectedCall?.outcome]?.label || 'Other'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span className="flex items-center space-x-1">
                            <Icon name="Clock" size={14} />
                            <span>{formatDuration(selectedCall?.duration_seconds)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="PhoneIncoming" size={14} />
                            <span>Inbound</span>
                          </span>
                          {selectedCall?.recording_url && (
                            <a
                              href={selectedCall?.recording_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-violet-600 hover:underline"
                            >
                              <Icon name="Play" size={14} />
                              <span>Recording</span>
                            </a>
                          )}
                        </div>
                      </div>

                      <div className="p-5 space-y-5">
                        {selectedCall?.ai_summary && (
                          <div>
                            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
                              <Icon name="Sparkles" size={14} className="text-violet-500" />
                              <span>Call Summary</span>
                            </h3>
                            <div className="p-4 bg-violet-50 border border-violet-100 rounded-xl">
                              <p className="text-sm text-foreground leading-relaxed">{selectedCall?.ai_summary}</p>
                            </div>
                          </div>
                        )}

                        {fetchingTranscript ? (
                          <div className="flex items-center justify-center py-8 space-x-2">
                            <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                            <p className="text-sm text-muted-foreground">Loading transcript...</p>
                          </div>
                        ) : selectedCall?.transcript ? (
                          <div>
                            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
                              <Icon name="FileText" size={14} className="text-muted-foreground" />
                              <span>Transcript</span>
                            </h3>
                            <div className="p-4 bg-muted rounded-xl max-h-64 overflow-y-auto">
                              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{selectedCall?.transcript}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No transcript available for this call.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-card border border-border rounded-xl flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                        <Icon name="Phone" size={28} className="text-violet-500" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground mb-1">Select a call</h3>
                      <p className="text-sm text-muted-foreground">Choose a call from the list to view transcript and summary.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AIVoicePage;
